import { ReactNode } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

type Variant =
  | 'info'
  | 'danger'
  | 'question'
  | 'success'
  | 'thumb-down'
  | 'thumb-up'
  | 'plan-upgrade';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',

    backgroundColor: theme.colors.blueMuted,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
    '&.variant--danger': {
      backgroundColor: theme.colors.redMuted,
    },
    '&.variant--success': {
      backgroundColor: theme.colors.greenMuted,
    },
    '&.variant--thumb-down': {
      backgroundColor: theme.colors.redMuted,
    },
    '&.variant--thumb-up': {
      backgroundColor: theme.colors.greenMuted,
    },
    '&.variant--question': {
      backgroundColor: theme.colors.grayBackground,
    },
    '&.variant--plan-upgrade': {
      backgroundColor: theme.colors.accentMuted,
    },
  },
  icon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.blue,
    marginRight: theme.marginBase,
    lineHeight: '100%',

    '.variant--danger &': {
      color: theme.colors.redAccessible,
    },
    '.variant--success &': {
      color: theme.colors.greenAccessible,
    },
    '.variant--thumb-down &': {
      color: theme.colors.redAccessible,
    },
    '.variant--thumb-up &': {
      color: theme.colors.greenAccessible,
    },
    '.variant--question &': {
      color: theme.colors.inkSecondary,
    },
    '.variant--plan-upgrade &': {
      color: theme.colors.accentAccessible,
    },
  },
  content: {
    ...theme.fonts.caption,
    flex: 1,
    color: theme.colors.inkPrimary,
  },
  title: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase,
    color: theme.colors.inkPrimary,
  },

  children: {
    ...theme.fonts.caption,
    flex: 1,
    color: theme.colors.inkPrimary,
    whiteSpace: 'pre-line',
  },
  withMargin: {
    marginTop: theme.marginBase * 3,
  },
}));

export interface InformationBoxProps {
  className?: string;
  variant?: Variant;
  title?: ReactNode;
  children?: ReactNode;
  text?: ReactNode;
  withMargin?: boolean;
  noIcon?: boolean;
  icon?: string;
}

export const InformationBox = ({
  className,
  variant = 'info',
  icon,
  title,
  children,
  withMargin,
  noIcon,
  text,
}: InformationBoxProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  return (
    <div
      className={classnames(
        classes.container,
        `variant--${variant}`,
        {
          [classes.withMargin]: withMargin,
        },
        className,
      )}
    >
      {!noIcon && (
        <i
          className={classnames(
            classes.icon,
            {
              'ri-information-line': variant === 'info' || variant === 'danger',
              'ri-checkbox-circle-fill': variant === 'success',
              'ri-question-line': variant === 'question',
              'ri-thumb-down-line': variant === 'thumb-down',
              'ri-thumb-up-line': variant === 'thumb-up',
              'ri-sparkling-2-line': variant === 'plan-upgrade',
            },
            icon,
          )}
        />
      )}
      <div className={classes.content}>
        {!!title && <div className={classes.title}>{title}</div>}
        {text}
        {!!children && <div className={classes.children}>{children}</div>}
      </div>
    </div>
  );
};
